/**
 * Similar to the structure of ../../JobDrawer
 * The hierarchy of components here is:
 * CreateTriggerDrawer - thin wrapper to allow animated drawer transition without loading content
 * CreateTriggerDrawerInner - queries for data, shows loading indicator
 * CreateTriggerDrawerLoaded - UI the user interacts with
 */

import {Drawer} from '@mui/material';
import React, {useCallback, useContext, useEffect, useState} from 'react';

import {
  useComputeAgentChatAvailability,
  useWbAgentClient,
} from '../../../../aria';
import {useHandleScroll} from '../../../../pages/HomePage/HomePageSidebar/useHandleScroll';
import {useIsAutomationAriaTriggerEnabled} from '../../../../util/rampFeatureFlags/rampFeatureFlags';
import {ResizableDrawer} from '../../../common/ResizableDrawer/ResizableDrawer';
import {AccountSelectorContext} from '../../../Search/SearchNav/AccountSelectorContextProvider';
import {useActionConfigStore} from './ActionStore';
import {
  AutomationStoreActions,
  type ResetStoresCallback,
} from './AutomationStore';
import * as S from './CreateTriggerDrawer.styles';
import {CreateTriggerDrawerLoaded} from './CreateTriggerDrawerLoaded';
import {type CreateTriggerDrawerInit, EventStoreActions} from './EventStore';
import {type TriggerFeatureFlags} from './types';

type CreateTriggerDrawerProps = {
  open: boolean;
  closeDrawer: () => void;
  createTriggerDrawerInit: CreateTriggerDrawerInit;
  refetchProjectTriggers: () => Promise<any>;
  refetchCollectionTriggers?: () => Promise<any>;
  isWeaveOnly?: boolean;
};

export const getResetStores =
  (
    init: CreateTriggerDrawerInit,
    onCloseInternal: () => void,
    doAsyncInit: (
      featureFlags: TriggerFeatureFlags,
      init: CreateTriggerDrawerInit,
      onCloseInternal: () => void
    ) => Promise<void>,
    isWeaveOnly = false,
    featureFlags: TriggerFeatureFlags = {}
  ): ResetStoresCallback =>
  () => {
    EventStoreActions.initAsync(init, isWeaveOnly);
    doAsyncInit(featureFlags, init, onCloseInternal);
  };

export const CreateTriggerDrawer = ({
  refetchProjectTriggers,
  refetchCollectionTriggers,
  createTriggerDrawerInit,
  open,
  closeDrawer,
  isWeaveOnly = false,
}: CreateTriggerDrawerProps) => {
  const {scrollHeight} = useHandleScroll();
  const [width, setWidth] = useState(600); // Remember last size
  const onSetWidth = (newWidth: number) => {
    setWidth(newWidth);
  };

  const onCloseInternal = useCallback(() => {
    closeDrawer();
  }, [closeDrawer]);

  const {selectedAccount} = useContext(AccountSelectorContext);

  const doAsyncInit = useActionConfigStore(s => s.doAsyncInit);

  // Returns false while loading; when the flag resolves, the effect below re-inits.
  const ariaFlagEnabled =
    useIsAutomationAriaTriggerEnabled(selectedAccount?.id) ?? false;
  // Product eligibility (SaaS org + LLM features + workspace-agent gate).
  // Computed here rather than via useAgentChatAvailability — AgentChatProvider
  // is only mounted inside the chat widget, so the shared store is not
  // authoritative on Automations pages.
  const {
    isAvailable: isWbAgentChatAvailable,
    unavailableReason: wbAgentUnavailableReason,
  } = useComputeAgentChatAvailability();
  // Service reachability: null when CONFIG.WB_AGENT_BASE_URL is unset (typical
  // for dedicated / local without wb_agent). Same gate as Generate with ARIA.
  const wbAgentClient = useWbAgentClient();
  const wbAgentAvailabilityLoading = wbAgentUnavailableReason === 'loading';
  const ariaTriggerEnabled =
    ariaFlagEnabled && isWbAgentChatAvailable && wbAgentClient != null;

  const featureFlags: TriggerFeatureFlags = {
    ariaTriggerEnabled,
  };

  useEffect(() => {
    // This useEffect ensures that we reset action config each time the drawer opens
    const allFlagsLoaded = !wbAgentAvailabilityLoading;
    if (open && allFlagsLoaded) {
      // note that we are calling an async function here, but not await'ing
      // This is on purpose - later code in this function does not rely on this running to completion.
      const resetStores = getResetStores(
        createTriggerDrawerInit,
        onCloseInternal,
        doAsyncInit,
        isWeaveOnly,
        featureFlags
      );
      resetStores();
      AutomationStoreActions.init(createTriggerDrawerInit, resetStores);
    }
    // we should only trigger this when open changes, so ignoring changes to the rest of the
    // dependencies.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    isWeaveOnly,
    wbAgentAvailabilityLoading,
    ariaTriggerEnabled,
    // in the workspace panel, we want to allow users to change the suggested metric name
    // by selecting different panels, so allow this property to cause us to run the init functions
    createTriggerDrawerInit.suggestedMetricName,
  ]);

  return (
    <Drawer anchor="right" open={open} onClose={onCloseInternal}>
      <ResizableDrawer
        startWidth={width}
        minWidth={600}
        onSetWidth={onSetWidth}>
        <S.DrawerContents scrollHeight={scrollHeight}>
          <CreateTriggerDrawerLoaded
            onClose={onCloseInternal}
            refetchProjectTriggers={refetchProjectTriggers}
            refetchCollectionTriggers={refetchCollectionTriggers}
          />
        </S.DrawerContents>
      </ResizableDrawer>
    </Drawer>
  );
};
